import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X } from "lucide-react";
import { useSite } from "@/lib/site";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

const ICONS = ["car", "paintbrush", "wrench", "truck", "settings", "hammer"];

const EMPTY = { title_en: "", title_es: "", description_en: "", description_es: "", image_url: "", icon: "wrench", order: 0 };

export default function ServicesEditor({ onChanged }) {
  const { services, reload } = useSite();
  const [editing, setEditing] = useState(null); // service id or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing && editing !== "new") {
      const s = services.find((x) => x.id === editing);
      if (s) setForm({ ...s });
    } else if (editing === "new") {
      setForm({ ...EMPTY, order: (services?.length || 0) + 1 });
    }
  }, [editing, services]);

  const refresh = async () => { await reload(); onChanged?.(); };

  const onSave = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/services", form);
        toast.success("Service added");
      } else {
        await api.put(`/services/${editing}`, form);
        toast.success("Service updated");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight">Services</h2>
          <div className="tech-label">Manage what your shop offers</div>
        </div>
        <Button onClick={() => setEditing("new")} className="bg-brand-red hover:bg-brand-redHover rounded-none" data-testid="service-add">
          <Plus size={14} className="mr-2" /> Add new
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services?.map((s, idx) => (
          <div key={s.id} className="bg-white border border-zinc-300 p-4 flex gap-3" data-testid={`service-card-${idx}`}>
            <div className="h-20 w-20 flex-shrink-0 bg-zinc-100 border border-zinc-200 overflow-hidden">
              {s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-base tracking-tight truncate">{s.title_en}</div>
              <div className="text-xs text-zinc-500 truncate">{s.title_es}</div>
              <div className="tech-label !text-[10px] mt-1">Order: {s.order}</div>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => setEditing(s.id)} className="text-xs font-bold text-zinc-700 hover:text-brand-red px-2 py-1" data-testid={`service-edit-${idx}`}>Edit</button>
              <button onClick={() => onDelete(s.id)} className="text-xs font-bold text-red-600 hover:text-red-800 px-2 py-1 inline-flex items-center gap-1" data-testid={`service-delete-${idx}`}>
                <Trash2 size={12} /> Del
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white max-w-2xl w-full border border-zinc-300 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-5 border-b border-zinc-300 sticky top-0 bg-white z-10">
              <h3 className="font-display font-black text-xl tracking-tight">
                {editing === "new" ? "New Service" : "Edit Service"}
              </h3>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-900"><X /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="tech-label mb-1 block">Title (EN)</label>
                  <Input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="rounded-none h-10" />
                </div>
                <div>
                  <label className="tech-label mb-1 block">Title (ES)</label>
                  <Input value={form.title_es} onChange={(e) => setForm({ ...form, title_es: e.target.value })} className="rounded-none h-10" />
                </div>
                <div>
                  <label className="tech-label mb-1 block">Description (EN)</label>
                  <Textarea rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="rounded-none" />
                </div>
                <div>
                  <label className="tech-label mb-1 block">Description (ES)</label>
                  <Textarea rows={3} value={form.description_es} onChange={(e) => setForm({ ...form, description_es: e.target.value })} className="rounded-none" />
                </div>
                <div>
                  <label className="tech-label mb-1 block">Icon</label>
                  <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                    <SelectTrigger className="rounded-none h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ICONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="tech-label mb-1 block">Display order</label>
                  <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="rounded-none h-10" />
                </div>
              </div>
              <ImageUpload testId="service-image" label="Service image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-zinc-300 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none">Cancel</Button>
              <Button onClick={onSave} disabled={saving} className="bg-brand-red hover:bg-brand-redHover rounded-none" data-testid="service-save">
                <Save size={14} className="mr-2" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
